import { DeleteDialog } from '@/components/dialogs/DeleteDialog';
import { capture } from '@/handlers/error';
import { useClassroom } from '@/hooks/useClassroom/useClassroom';
import { useLesson } from '@/hooks/useLesson/useLesson';
import { useLessonFolder } from '@/hooks/useLessonFolder/useLessonFolder';
import { useLessonFolders } from '@/hooks/useLessonFolders/useLessonFolders';
import { useLessons } from '@/hooks/useLessons/useLessons';
import { CreateSlidesVariables } from '@/hooks/useSlides/gql';
import { useSlides } from '@/hooks/useSlides/useSlides';
import { Class } from '@/models/class';
import { Lesson } from '@/models/lesson';
import { LessonFolder } from '@/models/lessonFolder';
import { useSession } from '@/providers/session/SessionProvider';
import { SlideComponent, Slide as SlideData, parseSlide } from '@/types/slide';
import { useLazyQuery } from '@apollo/client';
import { TabContext } from '@mui/lab';
import { Backdrop, Box, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { LessonsTabPanel, a11yProps } from './LessonsTabPanel';
import { ContextualToolbar } from './components/actions/ContextualToolbar';
import { LessonCreateDialog } from './components/actions/LessonCreateDialog';
import { LessonMoveDialog } from './components/actions/LessonMoveDialog';
import { LessonFolderCreateDialog } from './components/lessons/LessonFolderCreateDialog';
import { LessonPageHeader } from './components/lessons/LessonPageHeader';
import { LessonsTab } from './components/lessons/LessonsTab';
import { GET_LESSON, GetLessonResponse, GetLessonVariables } from './gql';
import { ContextualItem } from './type';

export const TAB_MY_LESSONS = 0;
export const TAB_SHARED_WITH_ME = 1;

export const LESSON_PAGE_SIZE = 24;
export const FOLDER_PAGE_SIZE = 12;

export default function LessonsPage() {
    // params
    const [searchParams] = useSearchParams();
    const folderParam: string = searchParams.get('folder') ?? '';
    const currentFolderParam: string = !window.history.state?.folder ? '' : folderParam;

    const tabParam = searchParams.get('tab') ?? 0;
    const tabState = tabParam ? Number(tabParam) : (window.history.state?.tab ?? 0);

    // use key to force remount and reset state when folder changes
    return (
        <div key={currentFolderParam + tabState}>
            <LessonsPageContent />
        </div>
    );
}

function LessonsPageContent() {
    // params
    const [searchParams] = useSearchParams();
    const folderParam: string = searchParams.get('folder') ?? '';
    const currentFolderParam: string = !window.history.state?.folder ? '' : folderParam;

    const tabParam = searchParams.get('tab') ?? 0;
    const tabState = tabParam ? Number(tabParam) : (window.history.state?.tab ?? 0);

    // provider
    const { session } = useSession();
    const { t } = useTranslation();
    const { add: addSlide, duplicate: duplicateSlide } = useSlides();
    const { start } = useClassroom();
    const notifications = useNotifications();
    const navigate = useNavigate();

    // ui
    const [tab, setTab] = useState<number>(tabState ?? 0);
    const [isDuplicatingLesson, setIsDuplicatingLesson] = useState(false);
    const [lessonCreateFolderDialogData, setLessonCreateFolderDialogData] = useState<LessonFolder | null>();
    const [showLessonCreationDialog, setShowLessonCreationDialog] = useState(false);
    const [lessons, setLessons] = useState<Lesson[] | undefined>(undefined);
    const [folders, setFolders] = useState<LessonFolder[] | undefined>(undefined);
    const [currentLessonsPage, setCurrentLessonsPage] = useState(0);
    const [isLoadingMoreLessons, setIsLoadingMoreLessons] = useState(false);
    const [selectedItems, setSelectedItems] = useState<ContextualItem[]>([]);
    const [showBulkRemoveDialog, setShowBulkRemoveDialog] = useState<boolean>(false);
    const [showBulkUnshareDialog, setShowBulkUnshareDialog] = useState<boolean>(false);
    const [showBulkMoveDialog, setShowBulkMoveDialog] = useState<boolean>(false);
    const [currentFolderPage, setCurrentFolderPage] = useState(0);
    const [isLoadingMoreFolders, setIsLoadingMoreFolders] = useState(false);

    // query related
    const [fetchLesson] = useLazyQuery<GetLessonResponse, GetLessonVariables>(GET_LESSON);
    const isSharedWithMeTabSelected = tab === TAB_SHARED_WITH_ME;

    const sharesFilter = useMemo(() => {
        if (!session?.user?.teacherProfile?.id) return [];

        return isSharedWithMeTabSelected
            ? {
                  teacher: { id: { not: { equals: session?.user?.teacherProfile?.id } } },
              }
            : {
                  teacher: { id: { equals: session?.user?.teacherProfile?.id } },
              };
    }, [session?.user?.teacherProfile?.id, isSharedWithMeTabSelected]);

    const foldersFilter = useMemo(() => {
        if (currentFolderParam === '') {
            return {
                OR: [{ folder: null }, { shares: { some: {} } }],
            };
        }

        return {
            folder: { id: { equals: currentFolderParam } },
        };
    }, [currentFolderParam]);

    const lessonsFilter = useMemo(() => {
        return {
            AND: [{ lessonVersions: { some: { published: { equals: false } } } }, { archived: { equals: false } }, foldersFilter, sharesFilter ?? []],
        };
    }, [foldersFilter, sharesFilter]);

    const { unshare: removeShareLessonAccess } = useLesson();

    const {
        lessons: paginatedLessons,
        create: createLesson,
        move: moveLesson,
        remove: removeLessons,
        update: updateLesson,
        refetch: refetchLessons,
        total: totalLessonsCount,
        getLessonsByFolders,
    } = useLessons(lessonsFilter, { size: LESSON_PAGE_SIZE, index: currentLessonsPage });
    const {
        folders: paginatedFolders,
        create: createFolder,
        update: updateFolder,
        remove: removeFolders,
        refetch: refetchFolders,
        total: totalFoldersCount,
    } = useLessonFolders(sharesFilter, { size: FOLDER_PAGE_SIZE, index: currentFolderPage });
    const { lessonFolder: currentFolder, unshare: removeShareFolderAccess } = useLessonFolder(currentFolderParam);

    const handleDuplicateLesson = useCallback(
        async (lesson: Lesson, toFolderId: string): Promise<Lesson | null> => {
            try {
                setIsDuplicatingLesson(true);
                // fetch the latest lesson
                const lessonResult = await fetchLesson({
                    variables: {
                        where: {
                            id: lesson.id,
                        },
                    },
                    fetchPolicy: 'network-only',
                });

                // duplicate lesson
                const newLesson = await createLesson(toFolderId, lessonResult.data?.lesson.latestLessonDraft.lessonName + ' (copy)');

                const duplicateVariables: CreateSlidesVariables['data'] = [];

                for (const slide of lessonResult.data?.lesson.latestLessonDraft.slides ?? []) {
                    // Deep clone to ensure we're not modifying the original slide data from the list
                    const modifiedSlideData: SlideData = parseSlide(JSON.parse(JSON.stringify(slide)));

                    if (!modifiedSlideData.data || !Array.isArray(modifiedSlideData.data)) {
                        continue;
                    }

                    // Regenerate IDs for components and their nested elements
                    modifiedSlideData.data = modifiedSlideData.data.map((component): SlideComponent => {
                        const newComponentId = crypto.randomUUID();

                        return {
                            ...component,
                            id: newComponentId,
                        };
                    });

                    duplicateVariables.push({
                        lessonVersion: { connect: { id: newLesson.latestLessonDraft.id } },
                        data: JSON.stringify(modifiedSlideData.data),
                        sort: slide.sort,
                    });
                }

                // Process duplicateVariables in chunks of 10
                const CHUNK_SIZE = 10;
                for (let i = 0; i < duplicateVariables.length; i += CHUNK_SIZE) {
                    const chunk = duplicateVariables.slice(i, i + CHUNK_SIZE);
                    await duplicateSlide(chunk);
                }

                notifications.show(t('lesson.notification.lessonCreated'), { severity: 'success', autoHideDuration: 5000 });

                return newLesson;
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setIsDuplicatingLesson(false);
            }
            return null;
        },
        [createLesson, duplicateSlide, fetchLesson, notifications, t],
    );

    const handleLoadMoreLessons = useCallback(async () => {
        setCurrentLessonsPage((prevPage) => {
            if (isLoadingMoreLessons) {
                return prevPage;
            }
            if (totalLessonsCount && (prevPage + 1) * LESSON_PAGE_SIZE >= totalLessonsCount) {
                return prevPage;
            } else {
                setIsLoadingMoreLessons(true);
                return prevPage + 1;
            }
        });
    }, [isLoadingMoreLessons, totalLessonsCount]);

    const handleLoadMoreFolders = useCallback(async () => {
        setCurrentFolderPage((prevPage) => {
            if (isLoadingMoreFolders) {
                return prevPage;
            }
            if (totalFoldersCount && (prevPage + 1) * FOLDER_PAGE_SIZE >= totalFoldersCount) {
                return prevPage;
            } else {
                setIsLoadingMoreFolders(true);
                return prevPage + 1;
            }
        });
    }, [isLoadingMoreFolders, totalFoldersCount]);

    useEffect(() => {
        if (paginatedLessons) {
            setLessons((prevLessons) => {
                const prevLessonIds = prevLessons?.map((lesson) => lesson.id);

                // add new lessons to the list
                const lessons = prevLessons ? [...prevLessons, ...paginatedLessons.filter((lesson) => !prevLessonIds?.includes(lesson.id))] : [...paginatedLessons];

                // update the lessons list with the new lessons (if it exists)
                const updatedLessons = paginatedLessons.filter((lesson) => prevLessonIds?.includes(lesson.id));
                for (const lesson of updatedLessons) {
                    const lessonIndex = lessons.findIndex((l) => l.id === lesson.id);
                    if (lessonIndex !== -1) {
                        lessons[lessonIndex] = lesson;
                    }
                }

                return lessons;
            });
            setIsLoadingMoreLessons(false);
        }
    }, [paginatedLessons]);

    const mergeFolders = useCallback((data: LessonFolder[]) => {
        if (data) {
            // accumulate folders without duplicates
            setFolders((prev) => {
                const existingIds = new Set(prev?.map((folder) => folder.id));
                const newFolders = data.filter((folder) => !existingIds.has(folder.id));
                return [...(prev ?? []), ...newFolders];
            });
        }
    }, []);

    useEffect(() => {
        if (paginatedFolders) {
            mergeFolders(paginatedFolders);
            setIsLoadingMoreFolders(false);
        }
    }, [paginatedFolders, mergeFolders]);

    const foldersExcludingCurrent = useMemo(() => {
        // Hide folders when inside a folder (no sub-folders feature)
        if (currentFolderParam) {
            return [];
        }

        return folders?.filter((folder) => folder.id !== currentFolderParam);
    }, [folders, currentFolderParam]);

    const breadcrumbsArray: {
        title: string;
        path: string;
    }[] = useMemo(() => {
        if (!currentFolderParam || !currentFolder) {
            return [];
        }
        return [
            {
                title: currentFolder.name,
                path: '',
            },
        ];
    }, [currentFolderParam, currentFolder]);

    const handleUpdateFolder = useCallback(
        async (
            result: {
                name: string;
            } | null,
            selectedFolder: LessonFolder,
        ) => {
            if (result) {
                try {
                    const updatedFolder = await updateFolder(selectedFolder.id, result.name);
                    if (updatedFolder) {
                        // replace updated folder in the list
                        setFolders((prevFolders) => (prevFolders ? prevFolders.map((folder) => (folder.id === updatedFolder.id ? updatedFolder : folder)) : [updatedFolder]));
                    }

                    notifications.show(t('lesson.notification.lessonFolderRenamed', { prevName: selectedFolder.name, latestName: result.name }), {
                        severity: 'success',
                        autoHideDuration: 5000,
                    });
                } catch (error) {
                    capture(error);
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                }
            }
        },
        [notifications, t, updateFolder],
    );

    const handleDeleteFolder = useCallback(
        async (
            confirm: boolean,
            selectedFolder: {
                name: string;
                ids: string[];
            },
        ) => {
            if (confirm) {
                if (selectedFolder) {
                    try {
                        // Collect all unique folder IDs including descendants
                        const folderIds = new Set(selectedFolder.ids);

                        const lessons = await getLessonsByFolders(Array.from(folderIds));

                        await removeLessons(lessons.map((lesson) => lesson.id));
                        const removedFolders = await removeFolders(Array.from(folderIds));
                        if (removedFolders) {
                            // remove deleted folders from the list
                            setFolders((prevFolders) => prevFolders?.filter((folder) => !folderIds.has(folder.id)));
                            await refetchFolders();
                        }

                        notifications.show(t('lesson.notification.lessonFolderRemoved'), {
                            severity: 'success',
                            autoHideDuration: 5000,
                        });
                    } catch (error) {
                        capture(error);
                        notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                    }
                }
            }
        },
        [getLessonsByFolders, removeLessons, removeFolders, notifications, t, refetchFolders],
    );

    const handleDeleteShareFolder = useCallback(
        async (
            confirm: boolean,
            selectedFolder: {
                name: string;
                folderId: string;
                shareIds: string[];
            },
        ) => {
            if (confirm) {
                if (selectedFolder) {
                    try {
                        // Collect all unique share folder IDs including descendants
                        const folderShareIds = new Set(selectedFolder.shareIds);
                        const removedFolder = await removeShareFolderAccess(Array.from(folderShareIds));
                        if (removedFolder && removedFolder.length > 0) {
                            // remove deleted folders from the list
                            setFolders((prevFolders) => prevFolders?.filter((folder) => folder.id !== selectedFolder.folderId));
                        }

                        notifications.show(t('lesson.notification.lessonFolderRemoved'), {
                            severity: 'success',
                            autoHideDuration: 5000,
                        });
                    } catch (error) {
                        capture(error);
                        notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                    }
                }
            }
        },
        [removeShareFolderAccess, notifications, t],
    );

    const handleDeleteShareLesson = useCallback(
        async (
            confirm: boolean,
            selectedLesson: {
                name: string;
                lessonId: string;
                shareIds: string[];
            },
        ) => {
            if (confirm) {
                if (selectedLesson) {
                    try {
                        const lessonShareIds = new Set(selectedLesson.shareIds);
                        const removedLesson = await removeShareLessonAccess(Array.from(lessonShareIds));

                        if (removedLesson && removedLesson.length > 0) {
                            // remove deleted lessons from the list
                            setLessons((prevLessons) => prevLessons?.filter((lesson) => lesson.id !== selectedLesson.lessonId));
                        }

                        notifications.show(t('lesson.notification.lessonRemoved', { lessonName: selectedLesson.name }), {
                            severity: 'success',
                            autoHideDuration: 5000,
                        });
                    } catch (error) {
                        capture(error);
                        notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                    }
                }
            }
        },
        [removeShareLessonAccess, notifications, t],
    );

    const handleStartLesson = useCallback(
        async (
            result: {
                name: string;
                class: Class;
                passlock?: string;
            } | null,
            selectedLesson: Lesson,
        ) => {
            if (result) {
                try {
                    const classroom = await start(selectedLesson, result.class, result.name, result.passlock);
                    navigate(`/classrooms/${classroom.id}`, { state: { classroomId: classroom.id }, viewTransition: true });
                } catch (error) {
                    capture(error);
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                }
            }

            return Promise.resolve();
        },
        [navigate, notifications, start],
    );

    const handleRenameLesson = useCallback(
        async (
            result: {
                name: string;
            } | null,
            selectedLesson: Lesson,
        ) => {
            if (result) {
                try {
                    const lesson = await updateLesson(selectedLesson, { name: result.name });
                    setLessons((prevLessons) => {
                        if (prevLessons) {
                            const lessonIndex = prevLessons.findIndex((lesson) => lesson.id === selectedLesson.id);
                            if (lessonIndex !== -1) {
                                const updatedLessons = [...prevLessons];
                                updatedLessons[lessonIndex] = lesson;
                                return updatedLessons;
                            }
                        }
                        return prevLessons;
                    });
                    notifications.show(t('lesson.notification.lessonRenamed', { prevName: selectedLesson.latestLessonDraft.lessonName, latestName: result.name }), {
                        severity: 'success',
                        autoHideDuration: 5000,
                    });
                } catch (error) {
                    capture(error);
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                }
            }
        },
        [notifications, t, updateLesson],
    );

    const handleRemoveLesson = useCallback(
        async (result: boolean, selectedLesson: Lesson) => {
            if (result) {
                try {
                    await removeLessons([selectedLesson.id]);
                    setLessons((prevLessons) => prevLessons?.filter((lesson) => lesson.id !== selectedLesson.id));
                    await refetchLessons();
                    notifications.show(t('lesson.notification.lessonRemoved', { lessonName: selectedLesson.latestLessonDraft.lessonName }), {
                        severity: 'success',
                        autoHideDuration: 5000,
                    });
                } catch (error) {
                    capture(error);
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                }
            }
        },
        [notifications, refetchLessons, removeLessons, t],
    );

    const handleMoveLesson = useCallback(
        async (value: LessonFolder | null, selectedLesson: Lesson) => {
            try {
                if ((value === null && !selectedLesson.folder) || value?.id === selectedLesson.folder?.id) {
                    return;
                }

                await moveLesson(selectedLesson.id, value ? value.id : null);
                setLessons((prevLessons) => prevLessons?.filter((lesson) => lesson.id !== selectedLesson.id));

                notifications.show(t('lesson.notification.lessonMoved', { lessonName: selectedLesson.latestLessonDraft.lessonName, folderName: value ? value.name : t('lesson.header') }), {
                    severity: 'success',
                    autoHideDuration: 5000,
                });
            } catch (error) {
                capture(error);
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            }
            return Promise.resolve();
        },
        [moveLesson, notifications, t],
    );

    const handleNavigateToFolder = useCallback(
        (folderId: string, tabIndex: number) => {
            navigate(`/lessons?folder=${folderId}&tab=${tabIndex}`, { viewTransition: true });
            history.replaceState({ ...history.state, folder: folderId }, '');
        },
        [navigate],
    );

    const handleOpenEditor = useCallback(
        (lesson: Lesson) => {
            navigate(`/lessons/${lesson.id}`, { state: { lessonId: lesson.id, prevPath: `/lessons/${lesson.id}` }, viewTransition: true });
        },
        [navigate],
    );

    const handleSelectFolder = useCallback((folder: LessonFolder) => {
        setSelectedItems((prev) => {
            if (prev.some((item) => item.id === folder.id && item instanceof LessonFolder)) {
                return prev.filter((item) => item.id !== folder.id || !(item instanceof LessonFolder));
            } else {
                return [...prev, folder];
            }
        });
    }, []);

    const handleSelectLesson = useCallback((lesson: Lesson) => {
        setSelectedItems((prev) => {
            if (prev.some((item) => item.id === lesson.id && item instanceof Lesson)) {
                return prev.filter((item) => item.id !== lesson.id || !(item instanceof Lesson));
            } else {
                return [...prev, lesson];
            }
        });
    }, []);

    const handleBulkRemove = useCallback(
        async (confirm: boolean) => {
            if (confirm) {
                try {
                    const selectedFolders = selectedItems.filter((item) => item instanceof LessonFolder);
                    const selectedLessons = selectedItems.filter((item) => item instanceof Lesson);
                    const folderIds = new Set(selectedFolders?.map((folder) => folder.id) ?? []);
                    const lessonIds = new Set(selectedLessons?.map((lesson) => lesson.id) ?? []);

                    // Get all lessons within all selected folders to remove
                    if (folderIds.size > 0) {
                        const folderLessons = await getLessonsByFolders(Array.from(folderIds));
                        folderLessons.forEach((lesson) => lessonIds.add(lesson.id));
                    }

                    // Remove all lessons (from folders and individually selected)
                    if (lessonIds.size > 0) {
                        await removeLessons(Array.from(lessonIds));
                        setLessons((prevLessons) => prevLessons?.filter((lesson) => !lessonIds.has(lesson.id)));

                        if (selectedLessons && selectedLessons.length >= LESSON_PAGE_SIZE) {
                            // Adjust lesson page index if the number of deleted lessons exceed the page size of the last page
                            setCurrentLessonsPage((index) => Math.max(0, index - Math.floor(selectedLessons.length / LESSON_PAGE_SIZE)));
                        } else {
                            // Else just refetch the last page
                            await refetchLessons();
                        }
                    }

                    // Remove all folders
                    if (folderIds.size > 0) {
                        const removedFolders = await removeFolders(Array.from(folderIds));
                        if (removedFolders) {
                            // Remove deleted folders from the list
                            setFolders((prevFolders) => prevFolders?.filter((folder) => !folderIds.has(folder.id)));

                            if (selectedFolders && selectedFolders.length >= FOLDER_PAGE_SIZE) {
                                // Adjust folder page index if the number of deleted folders exceed the page size of the last page
                                setCurrentFolderPage((index) => Math.max(0, index - Math.floor(selectedFolders.length / FOLDER_PAGE_SIZE)));
                            } else {
                                // Else just refetch the last page
                                await refetchFolders();
                            }
                        }
                    }

                    // reset
                    setSelectedItems([]);

                    notifications.show(t('lesson.notification.lessonBulkRemoved', { num: selectedItems.length }), {
                        severity: 'success',
                        autoHideDuration: 5000,
                    });
                } catch (error) {
                    capture(error);
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                }
            }
            setShowBulkRemoveDialog(false);
        },
        [selectedItems, notifications, t, getLessonsByFolders, removeLessons, refetchLessons, removeFolders, refetchFolders],
    );

    const handleBulkUnshare = useCallback(
        async (confirm: boolean) => {
            if (confirm) {
                try {
                    const selectedFolders = selectedItems.filter((item) => item instanceof LessonFolder);
                    const selectedLessons = selectedItems.filter((item) => item instanceof Lesson);
                    const folderIds = new Set(selectedFolders?.flatMap((folder) => folder.shares?.map((share) => share.id) ?? []) ?? []);
                    const lessonIds = new Set(selectedLessons?.flatMap((lesson) => lesson.shares?.map((share) => share.id) ?? []) ?? []);

                    // unshare all lessons
                    if (lessonIds.size > 0) {
                        const removedLessonShare = await removeShareLessonAccess(Array.from(lessonIds));
                        if (removedLessonShare && removedLessonShare.length > 0) {
                            // remove deleted lessons from the list
                            setLessons((prevLessons) => prevLessons?.filter((lesson) => !selectedLessons?.some((l) => l.id === lesson.id)));
                        }
                    }

                    // unshare all folders
                    if (folderIds.size > 0) {
                        const removedFolderShare = await removeShareFolderAccess(Array.from(folderIds));
                        if (removedFolderShare && removedFolderShare.length > 0) {
                            // remove deleted folders from the list
                            setFolders((prevFolders) => prevFolders?.filter((folder) => !selectedFolders?.some((f) => f.id === folder.id)));
                        }
                    }

                    // reset
                    setSelectedItems([]);

                    notifications.show(t('lesson.notification.lessonBulkRemoved', { num: selectedItems.length }), {
                        severity: 'success',
                        autoHideDuration: 5000,
                    });
                } catch (error) {
                    capture(error);
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                }
            }
            setShowBulkUnshareDialog(false);
        },
        [notifications, removeShareFolderAccess, removeShareLessonAccess, selectedItems, t],
    );

    const handleBulkMoveCancel = useCallback(() => {
        setShowBulkMoveDialog(false);
    }, []);

    const handleBulkMove = useCallback(
        async (value: LessonFolder | null) => {
            try {
                const selectedLessons = selectedItems.filter((item) => item instanceof Lesson);

                // filtered lessons that is at the same level of the destination
                const filteredLessons = selectedLessons.filter((lesson) => !((value === null && !lesson.folder) || value?.id === lesson.folder?.id));

                const lessonIds = new Set(filteredLessons?.map((lesson) => lesson.id) ?? []);

                await Promise.all([...Array.from(lessonIds).map((lessonId) => moveLesson(lessonId, value ? value.id : null))]);
                setLessons((prevLessons) => prevLessons?.filter((lesson) => !lessonIds.has(lesson.id)));

                if (filteredLessons && filteredLessons.length >= LESSON_PAGE_SIZE) {
                    // Adjust lesson page index if the number of deleted lessons exceed the page size of the last page
                    setCurrentLessonsPage((index) => Math.max(0, index - Math.floor(filteredLessons.length / LESSON_PAGE_SIZE)));
                } else {
                    // Else just refetch the last page
                    await refetchLessons();
                }

                // reset
                setSelectedItems([]);

                notifications.show(t('lesson.notification.lessonBulkMoved', { num: selectedItems.length, folderName: value ? value.name : t('lesson.header') }), {
                    severity: 'success',
                    autoHideDuration: 5000,
                });
            } catch (error) {
                capture(error);
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            }
            return Promise.resolve();
        },
        [moveLesson, notifications, refetchLessons, selectedItems, t],
    );

    const handleSharedFolder = useCallback((sharedFolderId: string, users: { id: string; email: string }[]) => {
        // update folder and add shared tag if sharedFolderId exists
        setFolders((prevFolders) => (prevFolders ? prevFolders.map((folder) => (folder.id === sharedFolderId ? new LessonFolder({ ...folder, shares: users }) : folder)) : []));
    }, []);

    const handleReplaceLesson = useCallback((lesson: Lesson) => {
        setLessons((prevLessons) =>
            prevLessons
                ? prevLessons.map((l) => {
                      if (l.id === lesson.id) {
                          lesson.latestLessonDraft.slides = l.latestLessonDraft.slides;
                          return lesson;
                      } else {
                          return l;
                      }
                  })
                : [],
        );
    }, []);

    const handleSharedLesson = useCallback(
        (lesson: Lesson) => {
            handleReplaceLesson(lesson);
        },
        [handleReplaceLesson],
    );

    const handleUnsharedLesson = useCallback(
        (lesson: Lesson) => {
            handleReplaceLesson(lesson);
        },
        [handleReplaceLesson],
    );

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 16 }}>
                <Stack sx={{ flex: 1 }} spacing={2}>
                    <LessonPageHeader
                        tab={tab}
                        defaultTitle={t('lesson.title')}
                        disableButtons={tab !== TAB_MY_LESSONS}
                        breadcrumbs={breadcrumbsArray}
                        currentFolder={currentFolder}
                        onCreateFolder={() => {
                            setLessonCreateFolderDialogData(new LessonFolder({ id: '', name: '', dateCreated: '', dateUpdated: '' }));
                        }}
                        onCreateLesson={() => setShowLessonCreationDialog(true)}
                    />

                    {selectedItems.length > 0 ? (
                        <ContextualToolbar
                            user={session?.user}
                            items={selectedItems}
                            onClearSelection={() => {
                                setSelectedItems([]);
                            }}
                            onMoveItems={() => {
                                setShowBulkMoveDialog(true);
                            }}
                            onRemoveItems={() => {
                                if (isSharedWithMeTabSelected) {
                                    setShowBulkUnshareDialog(true);
                                } else {
                                    setShowBulkRemoveDialog(true);
                                }
                            }}
                        />
                    ) : (
                        <TabContext value={tab}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs
                                    value={tab}
                                    onChange={(_event, newValue) => {
                                        setTab(newValue);
                                        setFolders(undefined);
                                        setLessons(undefined);
                                        setCurrentLessonsPage(0);
                                        setCurrentFolderPage(0);
                                        history.replaceState({ ...history.state, tab: newValue, folder: null }, '');
                                        setSelectedItems([]);
                                    }}
                                    aria-label='tabs'
                                    // 7.5px is a special value to vertically align the tabs menu with the AppBar
                                    // Adjust this value if the AppBar or the tabs menu height changes
                                    sx={{ mt: '7.5px' }}
                                >
                                    <Tab label={t('lesson.header')} {...a11yProps(TAB_MY_LESSONS)} />
                                    <Tab label={t('lesson.shareWithMe')} {...a11yProps(TAB_SHARED_WITH_ME)} />
                                </Tabs>
                            </Box>
                        </TabContext>
                    )}

                    <LessonsTabPanel value={tab} index={TAB_MY_LESSONS}>
                        <LessonsTab
                            tabIndex={tab}
                            lessons={lessons}
                            folderId={currentFolderParam}
                            folders={foldersExcludingCurrent}
                            selectedItems={selectedItems}
                            isLoadingMoreLessons={isLoadingMoreLessons}
                            isLoadingMoreFolders={isLoadingMoreFolders}
                            totalLessonsCount={totalLessonsCount}
                            totalFoldersCount={totalFoldersCount ?? 0}
                            onNavigateToFolder={handleNavigateToFolder}
                            onOpenEditor={handleOpenEditor}
                            onUpdateFolder={handleUpdateFolder}
                            onDeleteFolder={handleDeleteFolder}
                            onDeleteShareFolder={handleDeleteShareFolder}
                            onDeleteShareLesson={handleDeleteShareLesson}
                            onSharedFolder={handleSharedFolder}
                            onStartLesson={handleStartLesson}
                            onRenameLesson={handleRenameLesson}
                            onDuplicateLesson={handleDuplicateLesson}
                            onRemoveLesson={handleRemoveLesson}
                            onMoveLesson={handleMoveLesson}
                            onLoadMoreLessons={handleLoadMoreLessons}
                            onSelectFolder={handleSelectFolder}
                            onSelectLesson={handleSelectLesson}
                            onBulkRemove={handleBulkRemove}
                            onBulkUnshare={handleBulkUnshare}
                            onBulkMoveCancel={handleBulkMoveCancel}
                            onBulkMove={handleBulkMove}
                            onLoadMoreFolders={handleLoadMoreFolders}
                            onShareLesson={handleSharedLesson}
                            onUnshareLesson={handleUnsharedLesson}
                        />
                    </LessonsTabPanel>
                    <LessonsTabPanel value={tab} index={TAB_SHARED_WITH_ME}>
                        <LessonsTab
                            tabIndex={tab}
                            lessons={lessons}
                            folderId={currentFolderParam}
                            folders={foldersExcludingCurrent}
                            selectedItems={selectedItems}
                            isLoadingMoreFolders={isLoadingMoreFolders}
                            isLoadingMoreLessons={isLoadingMoreLessons}
                            totalLessonsCount={totalLessonsCount}
                            totalFoldersCount={totalFoldersCount ?? 0}
                            onNavigateToFolder={handleNavigateToFolder}
                            onOpenEditor={handleOpenEditor}
                            onUpdateFolder={handleUpdateFolder}
                            onDeleteFolder={handleDeleteFolder}
                            onDeleteShareFolder={handleDeleteShareFolder}
                            onDeleteShareLesson={handleDeleteShareLesson}
                            onStartLesson={handleStartLesson}
                            onRenameLesson={handleRenameLesson}
                            onDuplicateLesson={handleDuplicateLesson}
                            onRemoveLesson={handleRemoveLesson}
                            onMoveLesson={handleMoveLesson}
                            onLoadMoreLessons={handleLoadMoreLessons}
                            onSelectFolder={handleSelectFolder}
                            onSelectLesson={handleSelectLesson}
                            onBulkRemove={handleBulkRemove}
                            onBulkUnshare={handleBulkUnshare}
                            onBulkMoveCancel={handleBulkMoveCancel}
                            onBulkMove={handleBulkMove}
                            onLoadMoreFolders={handleLoadMoreFolders}
                            onShareLesson={handleSharedLesson}
                            onUnshareLesson={handleUnsharedLesson}
                        />
                    </LessonsTabPanel>
                </Stack>
            </div>

            {showLessonCreationDialog && (
                <LessonCreateDialog
                    open={showLessonCreationDialog}
                    onClose={async (data) => {
                        if (data) {
                            try {
                                const lesson = await createLesson(currentFolderParam, data.name);
                                setLessons((prevLessons) => (prevLessons ? [lesson, ...prevLessons] : [lesson]));
                                // add a first empty slide
                                await addSlide(lesson.latestLessonDraft.id, '', []);
                                notifications.show(t('lesson.notification.lessonCreated'), { severity: 'success', autoHideDuration: 5000 });
                            } catch (error) {
                                capture(error);
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                            }
                        }
                        setShowLessonCreationDialog(false);
                    }}
                    payload={undefined}
                />
            )}

            {lessonCreateFolderDialogData && (
                <LessonFolderCreateDialog
                    open={true}
                    onClose={async (result) => {
                        if (result) {
                            try {
                                const newFolder = await createFolder(result.name);
                                if (newFolder) {
                                    // prepend the new folder to the list if it doesn't already exist
                                    setFolders((prevFolders) => {
                                        if (prevFolders?.some((folder) => folder.id === newFolder.id)) {
                                            return prevFolders;
                                        }
                                        return prevFolders ? [newFolder, ...prevFolders] : [newFolder];
                                    });
                                }

                                notifications.show(t('lesson.notification.lessonFolderCreated', { folderName: result.name }), { severity: 'success', autoHideDuration: 5000 });
                            } catch (error) {
                                capture(error);
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                            }
                        }
                        setLessonCreateFolderDialogData(null);
                    }}
                    payload={{ data: { folder: lessonCreateFolderDialogData } }}
                />
            )}

            {showBulkRemoveDialog && (
                <DeleteDialog
                    title={t('common.remove') + ` "${t('lesson.actions.selected', { num: selectedItems.length })}"`}
                    description={t('lesson.bulk.delete.description')}
                    description2={''}
                    payload={null}
                    open={showBulkRemoveDialog}
                    onClose={handleBulkRemove}
                />
            )}

            {showBulkUnshareDialog && (
                <DeleteDialog
                    title={t('lesson.folder.actions.removeAccess') + ` "${t('lesson.actions.selected', { num: selectedItems.length })}"`}
                    description={t('lesson.folder.delete.removeItemAccessDescription', { user: session?.user?.email })}
                    description2={''}
                    payload={null}
                    open={showBulkUnshareDialog}
                    onClose={handleBulkUnshare}
                />
            )}

            {showBulkMoveDialog && (
                <LessonMoveDialog open={showBulkMoveDialog} title={t('lesson.actions.selected', { num: selectedItems.length })} onClose={handleBulkMoveCancel} onUpdate={handleBulkMove} />
            )}

            <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={isDuplicatingLesson}>
                <CircularProgress color='inherit' />
            </Backdrop>
        </>
    );
}
